import React, {useCallback, useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Button, Card, Col, Collapse, Descriptions, Input,
    Modal, Row, Skeleton, Space, Table, Tag, Typography, message,
} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import api from '../../services/api';
import ENDPOINTS from '../../constants/endpoints';
import {formatPath, ROUTES} from '../../constants/PathRoutes';
import {dateFormat} from '../../utils/utils';
import {GREE_1, PRESCRIPTION_STATUS_COLOR} from '../../utils/colors';
import PdfLink from '../../components/PdfLink';

const CLIENT_STATUS_COLOR = {
    UNDER_REVIEW: 'orange',
    APPROVED: 'green',
    WITH_ISSUES: 'red',
};

export default function AdminClientDetail() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [flagModal, setFlagModal] = useState(false);
    const [flagNotes, setFlagNotes] = useState('');

    const fetchClient = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(formatPath(ENDPOINTS.STAFF_CLIENT_DETAIL, {id}));
            setClient(res.data);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchClient();
    }, [fetchClient]);

    const handleApprove = async () => {
        setActionLoading('approve');
        try {
            await api.post(formatPath(ENDPOINTS.STAFF_CLIENT_APPROVE, {id}));
            message.success('Cliente aprovado com sucesso');
            fetchClient();
        } catch {
            message.error('Erro ao aprovar cliente');
        } finally {
            setActionLoading(null);
        }
    };

    const handleFlagOpen = () => {
        setFlagNotes(client.review_notes || '');
        setFlagModal(true);
    };

    const handleFlagConfirm = async () => {
        if (!flagNotes.trim()) {
            message.warning('Informe as observações');
            return;
        }
        setActionLoading('flag');
        try {
            await api.post(formatPath(ENDPOINTS.STAFF_CLIENT_FLAG, {id}), {notes: flagNotes.trim()});
            message.success('Cliente marcado com problemas');
            setFlagModal(false);
            fetchClient();
        } catch {
            message.error('Erro ao marcar cliente');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading || !client) {
        return <Skeleton active paragraph={{rows: 8}}/>;
    }

    const canApprove = ['UNDER_REVIEW', 'WITH_ISSUES'].includes(client.status);
    const canFlag = ['UNDER_REVIEW', 'APPROVED'].includes(client.status);

    const prescriptionColumns = [
        {
            title: 'Arquivo',
            dataIndex: 'prescription_file',
            key: 'prescription_file',
            render: (url) => <PdfLink url={url}/>,
        },
        {
            title: 'Desconto',
            dataIndex: 'discount_value',
            key: 'discount_value',
            render: (v) => (v ? `R$ ${Number(v).toFixed(2)}` : '-'),
        },
        {
            title: 'Validade',
            dataIndex: 'expiration_date',
            key: 'expiration_date',
            render: (date) => dateFormat(date) || '-',
        },
        {
            title: 'CRM',
            dataIndex: 'doctor_crm',
            key: 'doctor_crm',
            render: (v) => v || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status_display',
            key: 'status',
            render: (display, record) => (
                <Tag color={PRESCRIPTION_STATUS_COLOR[record.status] ?? 'default'}>{display ?? '-'}</Tag>
            ),
        },
    ];

    return (
        <>
            <Space style={{marginBottom: 20}}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined/>}
                    onClick={() => navigate(ROUTES.ADMIN_CLIENT_LIST)}
                    style={{padding: 0, color: GREE_1}}
                >
                    Clientes
                </Button>
            </Space>

            {/* Header */}
            <Card style={{marginBottom: 16}}>
                <Row justify="space-between" align="middle" wrap>
                    <Col>
                        <Space direction="vertical" size={2}>
                            <Typography.Title level={5} style={{margin: 0}}>
                                {client.first_name} {client.last_name}
                            </Typography.Title>
                            <Typography.Text type="secondary">{client.email}</Typography.Text>
                        </Space>
                    </Col>
                    <Col>
                        <Space wrap>
                            <Tag
                                color={CLIENT_STATUS_COLOR[client.status] ?? 'default'}
                                style={{fontSize: 13, padding: '4px 10px'}}
                            >
                                {client.status_display}
                            </Tag>
                            {canApprove && (
                                <Button
                                    type="primary"
                                    loading={actionLoading === 'approve'}
                                    onClick={handleApprove}
                                >
                                    Aprovar
                                </Button>
                            )}
                            {canFlag && (
                                <Button
                                    danger
                                    loading={actionLoading === 'flag'}
                                    onClick={handleFlagOpen}
                                >
                                    Marcar com Problemas
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>
                {client.review_notes && (
                    <div style={{
                        marginTop: 12,
                        padding: '8px 12px',
                        background: '#fff7e6',
                        borderRadius: 6,
                        borderLeft: '3px solid #faad14',
                    }}>
                        <Typography.Text strong>Observações: </Typography.Text>
                        <Typography.Text>{client.review_notes}</Typography.Text>
                    </div>
                )}
            </Card>

            <Row gutter={16} style={{marginBottom: 16}}>
                {/* Dados Pessoais */}
                <Col xs={24} lg={16}>
                    <Card title="Dados Pessoais">
                        <Descriptions column={{xs: 1, sm: 2}} size="small">
                            <Descriptions.Item label="CPF">{client.cpf || '-'}</Descriptions.Item>
                            <Descriptions.Item label="RG">{client.rg || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Nascimento">{dateFormat(client.birth_date) || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Gênero">{client.gender_display || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Telefone">{client.phone || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Profissão">{client.profession || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Tipo">{client.client_type_display || '-'}</Descriptions.Item>
                            <Descriptions.Item label="E-mail verificado">{client.email_verified ? 'Sim' : 'Não'}</Descriptions.Item>
                            <Descriptions.Item label="Cadastro">{dateFormat(client.date_joined) || '-'}</Descriptions.Item>
                            {/*<Descriptions.Item label="Último acesso">{client.last_login ? dateFormat(client.last_login) : '-'}</Descriptions.Item>*/}
                        </Descriptions>
                    </Card>
                </Col>

                {/* Documentos */}
                <Col xs={24} lg={8}>
                    <Card title="Documentos" style={{height: '100%'}}>
                        <Space direction="vertical">
                            <div>
                                <Typography.Text type="secondary" style={{display: 'block', marginBottom: 4}}>
                                    Termo Judicial
                                </Typography.Text>
                                <PdfLink url={client.judicial_term_pdf}/>
                            </div>
                            <div>
                                <Typography.Text type="secondary" style={{display: 'block', marginBottom: 4}}>
                                    Termo de Consentimento
                                </Typography.Text>
                                <PdfLink url={client.consent_term_pdf}/>
                            </div>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {/* Dependentes */}
            {client.dependents?.length > 0 && (
                <Card title="Dependentes">
                    <Collapse>
                        {client.dependents.map(dep => (
                            <Collapse.Panel
                                key={dep.id}
                                header={
                                    <Space>
                                        <span>{dep.first_name} {dep.last_name}</span>
                                        <Tag>{dep.kind_display}</Tag>
                                        {dep.is_self && <Tag color="blue">Titular</Tag>}
                                    </Space>
                                }
                            >
                                <Descriptions column={{xs: 1, sm: 2}} size="small" style={{marginBottom: 16}}>
                                    <Descriptions.Item label="CPF">{dep.cpf || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="RG">{dep.rg || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Nascimento">{dateFormat(dep.birth_date) || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Gênero">{dep.gender_display || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Condição Médica" span={2}>
                                        {dep.medical_condition || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Laudo Médico">
                                        <PdfLink url={dep.medical_report_pdf}/>
                                    </Descriptions.Item>
                                </Descriptions>

                                {dep.prescriptions?.length > 0 && (
                                    <>
                                        <Typography.Text strong style={{display: 'block', marginBottom: 8}}>
                                            Prescrições
                                        </Typography.Text>
                                        <Table
                                            dataSource={dep.prescriptions}
                                            columns={prescriptionColumns}
                                            rowKey="id"
                                            size="small"
                                            pagination={false}
                                            scroll={{x: 'max-content'}}
                                        />
                                    </>
                                )}
                            </Collapse.Panel>
                        ))}
                    </Collapse>
                </Card>
            )}

            {/* Modal de problemas */}
            <Modal
                title="Marcar com Problemas"
                open={flagModal}
                onOk={handleFlagConfirm}
                onCancel={() => setFlagModal(false)}
                okText="Confirmar"
                cancelText="Cancelar"
                okButtonProps={{danger: true, loading: actionLoading === 'flag'}}
            >
                <Space direction="vertical" style={{width: '100%', marginTop: 8}}>
                    <Typography.Text>Descreva os problemas encontrados no cadastro do cliente:</Typography.Text>
                    <Input.TextArea
                        rows={4}
                        placeholder="Ex: Documento judicial ilegível. Solicitar reenvio."
                        value={flagNotes}
                        onChange={e => setFlagNotes(e.target.value)}
                    />
                </Space>
            </Modal>
        </>
    );
}
